import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Calendar, Gavel, BarChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ProfileDropdown } from '../ProfileDropdown';
import { HyroxRacesTile } from './HyroxRacesTile';
import { HeadJudgesTimingTile } from './HeadJudgesTimingTile';
import { RegionEventStatus } from './RegionEventStatus';

const TimingDashboard: React.FC = () => {
  const [selectedTile, setSelectedTile] = useState<string | null>('race-timing');

  const handleTileClick = (tileName: string) => {
    setSelectedTile(selectedTile === tileName ? null : tileName);
  };

  return (
    <div className="min-h-screen bg-hyrox-light">
      <header className="bg-hyrox-dark border-b border-gray-800 px-6 py-4 shadow-lg">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <Link to="/dashboard">
              <img 
                src="/HyroxLogo.png" 
                alt="Hyrox Logo" 
                className="h-7 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
              />
            </Link>
          </div>
          <div className="ml-8">
            <ProfileDropdown />
          </div>
        </div>
      </header>
      
      <div className="w-full h-screen flex flex-col">
        {/* Main Layout - Sidebar + Content */}
        <div className="flex flex-1 p-4 gap-4 overflow-hidden">
          {/* Left Sidebar - 12% width */}
          <div className="w-[12%] flex flex-col space-y-4">
            {/* Race Timing Tile */}
            <Card 
              className={`cursor-pointer transition-all duration-200 w-full ${
                selectedTile === 'race-timing' 
                  ? 'ring-2 ring-black shadow-lg' 
                  : 'hover:shadow-md'
              }`}
              onClick={() => handleTileClick('race-timing')}
            >
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 bg-gray-300 rounded-lg">
                    <Calendar className="w-8 h-8 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Penalty Review & Process</h3>
                    <p className="text-sm text-gray-600">Process Penalties logged by Head Judges</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Head Judges Tile */}
            <Card 
              className={`cursor-pointer transition-all duration-200 w-full ${
                selectedTile === 'head-judges' 
                  ? 'ring-2 ring-black shadow-lg' 
                  : 'hover:shadow-md'
              }`}
              onClick={() => handleTileClick('head-judges')}
            >
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 bg-gray-300 rounded-lg">
                    <Gavel className="w-8 h-8 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Manage Head Judges</h3>
                    <p className="text-sm text-gray-600">Prepare Head Judges for Penalty Scoring</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Region Event Status Tile */}
            <Card 
              className={`cursor-pointer transition-all duration-200 w-full ${
                selectedTile === 'region-event-status' 
                  ? 'ring-2 ring-black shadow-lg' 
                  : 'hover:shadow-md'
              }`}
              onClick={() => handleTileClick('region-event-status')}
            >
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 bg-gray-300 rounded-lg">
                    <BarChart className="w-8 h-8 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Event Status by Region</h3>
                    <p className="text-sm text-gray-600">View metrics of penalties logged, categorized by region</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Content Area - 88% width */}
          <div className="w-[88%] overflow-hidden">
            {selectedTile === 'race-timing' && <HyroxRacesTile />}
            {selectedTile === 'head-judges' && <HeadJudgesTimingTile />}
            {selectedTile === 'region-event-status' && <RegionEventStatus />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimingDashboard;
